import React, { useState } from 'react';
import { Crown, Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (err) {
      setError('Failed to send message. Please try calling us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Crown className="w-16 h-16 text-yellow-400 pulse-gold" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">Get In Touch</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have questions? Ready to book? We're here to help you feel your best.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 px-4" data-testid="contact-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                <span className="gradient-text">Contact Information</span>
              </h2>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl flex-shrink-0">
                      <Phone className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400 mb-2">Phone</h3>
                      <a href="tel:727-503-2572" className="text-gray-300 hover:text-yellow-400 transition-colors text-lg">
                        727-503-2572
                      </a>
                      <p className="text-gray-400 text-sm mt-2">
                        Call or text us anytime
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl flex-shrink-0">
                      <Mail className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400 mb-2">Email</h3>
                      <a href="mailto:Vockmedical2006moe@gmail.com" className="text-gray-300 hover:text-yellow-400 transition-colors break-all">
                        Vockmedical2006moe@gmail.com
                      </a>
                      <p className="text-gray-400 text-sm mt-2">
                        We'll respond within 24 hours
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl flex-shrink-0">
                      <MapPin className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400 mb-2">Service Areas</h3>
                      <ul className="text-gray-300 space-y-1">
                        <li>• Pinellas County</li>
                        <li>• Hillsborough County</li>
                        <li>• Pasco County</li>
                        <li>• Greater Tampa Bay Area</li>
                      </ul>
                      <p className="text-xs text-gray-400 italic mt-3">
                        Additional counties available upon request — please contact us to discuss.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-400/20 p-3 rounded-xl flex-shrink-0">
                      <Clock className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400 mb-2">Hours</h3>
                      <p className="text-gray-300">
                        Mobile Service Available
                      </p>
                      <p className="text-yellow-400 font-medium mt-1">
                        Call to Schedule
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                <span className="gradient-text">Send Us a Message</span>
              </h2>

              {success ? (
                <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/30 rounded-2xl p-8 text-center" data-testid="contact-success-message">
                  <div className="text-6xl mb-4">✅</div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-2">Message Sent!</h3>
                  <p className="text-gray-300">
                    Thank you for reaching out. We'll get back to you shortly!
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-6 text-yellow-400 hover:text-yellow-300 font-medium"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-yellow-500/20 rounded-2xl p-8" data-testid="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="(123) 456-7890"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Message *
                      </label>
                      <textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows="6"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="Tell us what you need..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    {error && (
                      <div className="text-red-400 text-sm" data-testid="contact-error-message">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold py-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      data-testid="contact-submit-btn"
                    >
                      <Send className="w-5 h-5" />
                      <span>{loading ? 'Sending...' : 'Send Message'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-gray-900/50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="gradient-text">Frequently Asked Questions</span>
          </h2>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Do you come to my home?</h3>
              <p className="text-gray-300">
                Yes! We're a mobile service. We come to your home, office, hotel, or any location that's comfortable for you within our service area.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">How long does a session take?</h3>
              <p className="text-gray-300">
                Most IV therapy sessions take 30-45 minutes. You can relax, work, or enjoy your favorite show during treatment.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">How quickly will I feel results?</h3>
              <p className="text-gray-300">
                Many clients feel the effects within 30-45 minutes as the nutrients are delivered directly into your bloodstream with 100% absorption.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Is IV therapy safe?</h3>
              <p className="text-gray-300">
                Yes! All treatments are administered by a licensed Registered Nurse using pharmaceutical-grade vitamins and minerals. We follow strict safety protocols.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">How do I know which IV therapy is right for me?</h3>
              <p className="text-gray-300">
                Call us for a free consultation! We'll discuss your wellness goals and recommend the best treatment plan for your needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
